import React, { useState, useEffect, useMemo, useCallback } from "react";
import Sidebar from "./Sidebar";
import apiClient from "../api/client";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import "./Analytics.css";

import iconMenu from "../assets/icon-menu.svg";
import iconSearch from "../assets/icon-search.svg";
import NotificationBell from "./NotificationBell";
import ProfilePicture from "./ProfilePicture";

const CARD_ICONS = {
  calendar: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect x="3" y="5" width="14" height="12" rx="2" stroke="#14b8a6" strokeWidth="2" fill="none" />
      <path d="M3 8H17" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 3V5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M13 3V5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  clock: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7" stroke="#14b8a6" strokeWidth="2" fill="none" />
      <path d="M10 5V10L13 13" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  distance: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M4 16V8L10 4L16 8V16H4Z"
        stroke="#14b8a6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  streak: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2L12 8L18 10L12 12L10 18L8 12L2 10L8 8L10 2Z" fill="#14b8a6" />
    </svg>
  ),
  sun: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="3" stroke="#14b8a6" strokeWidth="2" fill="none" />
      <path d="M10 2V4" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M10 16V18" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 10H2" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M18 10H16" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M4.5 4.5L6 6" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M14 14L15.5 15.5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M4.5 15.5L6 14" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M14 6L15.5 4.5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  weather: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path
        d="M7 15C4.79 15 3 13.21 3 11C3 8.96 4.55 7.27 6.56 7.04C7.28 4.73 9.39 3 12 3C14.97 3 17.4 5.36 17.5 8.32C18.93 8.86 20 10.22 20 11.86C20 14.05 18.09 15.86 15.9 15.86H7Z"
        stroke="#14b8a6"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 18L8.5 16.5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M11 18L12.5 16.5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 18L16.5 16.5" stroke="#14b8a6" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

const ANALYTICS_SECTIONS = [
  {
    id: "patterns",
    title: "Training Patterns",
    description: "How often, when, and how far you tend to run.",
    cards: [
      {
        id: "day-of-week",
        icon: "calendar",
        badge: "high",
        value: "—",
        title: "Most Active Day",
        description: "Where your mileage naturally clusters through the week.",
      },
      {
        id: "time-of-day",
        icon: "clock",
        badge: "medium",
        value: "—",
        title: "Time-of-Day Rhythm",
        description: "The daily window that sees the most runs.",
      },
      {
        id: "distance-distribution",
        icon: "distance",
        badge: "medium",
        value: "—",
        title: "Distance Profile",
        description: "How your run distances stack up.",
        variant: "wide",
      },
    ],
  },
  {
    id: "streak-health",
    title: "Streak Health",
    description: "What keeps the streak alive and the environments that help.",
    cards: [
      {
        id: "streak-behavior",
        icon: "streak",
        badge: "high",
        value: "—",
        title: "Streak Behavior",
        description: "Consistency, fallback runs, and distance variability.",
      },
      {
        id: "daylight-patterns",
        icon: "sun",
        badge: "medium",
        value: "—",
        title: "Daylight Balance",
        description: "How often you run in daylight versus darkness.",
      },
      {
        id: "weather-patterns",
        icon: "weather",
        badge: "medium",
        value: "—",
        title: "Weather Readiness",
        description: "Conditions and temperatures that impact your runs.",
      },
    ],
  },
  {
    id: "milestones",
    title: "Milestones & Highlights",
    description: "Recent standout efforts from your streak history.",
    cards: [
      {
        id: "achievement-longest-run",
        icon: "distance",
        badge: "high",
        value: "—",
        title: "Longest Run",
        description: "Farthest effort logged during this streak.",
      },
      {
        id: "achievement-fastest-pace",
        icon: "clock",
        badge: "medium",
        value: "—",
        title: "Fastest Mile Pace",
        description: "Quickest sustained pace from completed runs.",
      },
      {
        id: "achievement-perfect-weeks",
        icon: "calendar",
        badge: "medium",
        value: "—",
        title: "Perfect Weeks",
        description: "Weeks where you ran every single day.",
      },
    ],
  },
];

const CARD_LOOKUP = ANALYTICS_SECTIONS.reduce((map, section) => {
  section.cards.forEach((card) => {
    map[card.id] = { ...card, sectionTitle: section.title };
  });
  return map;
}, {});

const DEFAULT_CARD_ID = ANALYTICS_SECTIONS[0]?.cards[0]?.id ?? null;

const isFiniteNumber = (value) => Number.isFinite(value);
// formatDistance is now defined inside buildAnalyticsContent to support unit conversion
const formatPercent = (value, digits = 0) =>
  isFiniteNumber(value) ? value.toFixed(digits) + "%" : "—";
const formatCount = (value) => (Number.isFinite(value) ? String(value) : "—");
const toTitleCase = (value) =>
  value
    ? value
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "";
const removeReplacementChars = (text) => (text || "").replace(/\uFFFD/g, "");
// Convert "miles" and "mi" in insight text to km based on unit preference
const convertInsightText = (text, unit) => {
  if (!text || unit !== "km") {
    return text;
  }
  // Replace patterns like "2.5 miles", "3 mile", "2-4 miles", "< 2 miles" with km equivalents
  return text.replace(/([<>]?\s*\d+\.?\d*(?:\s*-\s*\d+\.?\d*)?)\s*(miles?|mi)(?!\w)/g, (match, num, unit) => {
    // Extract numbers, handling ranges and comparison operators
    const numbers = num.match(/\d+\.?\d*/g);
    if (!numbers) return match;

    const converted = numbers.map(n => (parseFloat(n) * 1.60934).toFixed(1)).join('-');
    const prefix = num.match(/^[<>]?\s*/)?.[0] || '';
    return prefix.trim() + (prefix ? ' ' : '') + converted + ' km';
  });
};
const cleanInsights = (insights, unit) => {
  if (!Array.isArray(insights)) return [];
  return insights
    .map(removeReplacementChars)
    .filter(Boolean)
    .map(text => convertInsightText(text, unit));
};
const formatTemperatureBucket = (bucket) => {
  if (!isFiniteNumber(bucket)) {
    return null;
  }
  const start = bucket;
  const end = bucket + 5;
  return String(start) + "-" + String(end) + " C";
};
const noDataDetail = (message) => ({
  summary: message,
  metrics: [],
  insights: [],
  actions: ["Log more activities to unlock this insight."],
});
const parseNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const formatDateLong = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};
const formatCountLabel = (value, singular, plural) => {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const noun = value === 1 ? singular : plural;
  return value + " " + noun;
};
const getErrorMessage = (error, fallbackMessage) => {
  if (!error) {
    return fallbackMessage;
  }
  if (error.response && error.response.data && typeof error.response.data.error === "string") {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return fallbackMessage;
};

const buildAnalyticsContent = (analytics, achievements, convertFromMiles, getUnitLabel, unit) => {
  const cardData = {};
  const detailData = {};

  // Helper function to format distance with unit conversion
  const formatDistance = (value, digits = 1) => {
    if (!isFiniteNumber(value)) return "—";
    const converted = convertFromMiles(value);
    return converted.toFixed(digits) + " " + getUnitLabel();
  };

  // Helper to format pace with correct unit
  const formatPaceUnit = () => {
    return "min/" + getUnitLabel();
  };

  // Helper to convert pace from min/mile to user's preferred unit
  const convertPaceFromMinPerMile = (paceMinPerMile) => {
    if (!isFiniteNumber(paceMinPerMile)) return paceMinPerMile;
    if (unit === 'km') {
      return paceMinPerMile / 1.60934; // Convert min/mile to min/km
    }
    return paceMinPerMile; // Already in min/mile
  };

  // Helper to get distance word (mile/km)
  const getDistanceWord = () => {
    return getUnitLabel() === 'mi' ? 'mile' : 'km';
  };

  // Helper to format pace respecting unit preferences
  const formatPaceValue = (value, digits = 1) => {
    const converted = convertPaceFromMinPerMile(value);
    return isFiniteNumber(converted) && converted > 0
      ? converted.toFixed(digits) + " " + formatPaceUnit()
      : null;
  };

  if (!analytics) {
    return { cardData, detailData };
  }

  const addNoData = (id, message) => {
    detailData[id] = noDataDetail(message);
    cardData[id] = { value: "No data", change: null };
  };
  const dayOfWeek = analytics.day_of_week;
  if (dayOfWeek) {
    if (dayOfWeek.error) {
      const message = removeReplacementChars(dayOfWeek.error) || "We could not calculate your weekly pattern yet.";
      addNoData("day-of-week", message);
    } else {
      const entries = Object.entries(dayOfWeek.day_breakdown || {}).filter(([, stats]) => {
        return Number.isFinite(stats && stats.count) && stats.count > 0;
      });

      if (entries.length) {
        const byCount = entries.slice().sort((a, b) => (b[1].count || 0) - (a[1].count || 0));
        const topDayEntry = byCount[0];
        const longestEntry = entries.slice().sort((a, b) => (b[1].avg_distance || 0) - (a[1].avg_distance || 0))[0];
        const leastEntry = byCount[byCount.length - 1];
        const coverage = entries.length;

        const topDayName = topDayEntry[0];
        const topDayStats = topDayEntry[1];
        const longestName = longestEntry[0];
        const longestStats = longestEntry[1];
        const leastName = leastEntry[0];
        const leastStats = leastEntry[1];

        const summaryParts = [];
        var firstLine = "You run most often on " + topDayName + " (" + formatCount(topDayStats.count) + " runs";
        if (isFiniteNumber(topDayStats.percentage_of_runs)) {
          firstLine += ", " + topDayStats.percentage_of_runs.toFixed(1) + "% of your log";
        }
        firstLine += ").";
        summaryParts.push(firstLine);
        if (isFiniteNumber(longestStats.avg_distance)) {
          summaryParts.push(
            "Your longest average efforts land on " +
              longestName +
              " at " +
              formatDistance(longestStats.avg_distance) +
              "."
          );
        }
        const summary = summaryParts.join(" ");

        const metrics = [
          { label: "Most active day", value: topDayName + " • " + formatCount(topDayStats.count) + " runs" },
          { label: "Avg distance (most active)", value: formatDistance(topDayStats.avg_distance) },
          { label: "Longest day average", value: longestName + " • " + formatDistance(longestStats.avg_distance) },
          { label: "Week coverage", value: coverage + " of 7 days" },
        ];

        const insights = cleanInsights(dayOfWeek.insights, unit);
        const actions = [
          "Plan key workouts on " + longestName + " to leverage your natural mileage peak.",
        ];
        if (leastName !== topDayName && leastStats) {
          actions.push(
            "Keep " +
              leastName +
              " lighter with recovery or streak-saver runs (" +
              formatCount(leastStats.count) +
              " logged so far)."
          );
        } else {
          actions.push("Continue monitoring weekly balance to keep recovery spaces intact.");
        }

        detailData["day-of-week"] = {
          title: "Weekly rhythm",
          summary,
          metrics,
          insights,
          actions,
        };

        let changeValue = formatCount(topDayStats.count) + " runs";
        if (isFiniteNumber(topDayStats.percentage_of_runs)) {
          changeValue = topDayStats.percentage_of_runs.toFixed(1) + "% of runs";
        }

        cardData["day-of-week"] = {
          value: topDayName,
          change: {
            direction: "flat",
            value: changeValue,
            label: "share",
            tone: "neutral",
          },
        };
      } else {
        addNoData("day-of-week", "We need more activities to map your weekly pattern.");
      }
    }
  }
  const timeOfDay = analytics.time_of_day;
  if (timeOfDay) {
    if (timeOfDay.error) {
      const message = removeReplacementChars(timeOfDay.error) || "Time-of-day analytics are not available yet.";
      addNoData("time-of-day", message);
    } else {
      const entries = Object.entries(timeOfDay.time_breakdown || {}).filter(([, stats]) => {
        return Number.isFinite(stats && stats.count) && stats.count > 0;
      });

      if (entries.length) {
        const byCount = entries.slice().sort((a, b) => (b[1].count || 0) - (a[1].count || 0));
        const favoriteEntry = byCount[0];
        const favoriteKey = favoriteEntry[0];
        const favoriteStats = favoriteEntry[1];
        const favoriteName = toTitleCase(favoriteKey);
        const secondaryEntry = byCount[1];
        const distanceEntry = entries.slice().sort((a, b) => (b[1].avg_distance || 0) - (a[1].avg_distance || 0))[0];

        const summaryParts = [];
        summaryParts.push(
          "Your go-to window is the " +
            favoriteName +
            " (" +
            favoriteStats.range +
            ") capturing " +
            formatPercent(favoriteStats.percentage, 1) +
            " of runs."
        );
        if (distanceEntry && distanceEntry[0] !== favoriteKey && isFiniteNumber(distanceEntry[1].avg_distance)) {
          summaryParts.push(
            toTitleCase(distanceEntry[0]) +
              " sessions trend longest at " +
              formatDistance(distanceEntry[1].avg_distance) +
              "."
          );
        }
        const summary = summaryParts.join(" ");

        const metrics = [
          { label: "Favorite window", value: favoriteName + " • " + favoriteStats.range },
          { label: "Share of runs", value: formatPercent(favoriteStats.percentage, 1) },
          { label: "Avg distance", value: formatDistance(favoriteStats.avg_distance) },
        ];
        if (secondaryEntry) {
          metrics.push({
            label: "Secondary window",
            value: toTitleCase(secondaryEntry[0]) + " • " + secondaryEntry[1].range,
          });
        }

        const insights = cleanInsights(timeOfDay.insights, unit);
        const actions = [
          "Lock in key workouts during the " + favoriteName.toLowerCase() + " window when energy is highest.",
        ];
        if (secondaryEntry) {
          actions.push(
            "Use the " +
              toTitleCase(secondaryEntry[0]).toLowerCase() +
              " slot for recovery or cross-training to balance the week."
          );
        }

        detailData["time-of-day"] = {
          title: "Daily cadence",
          summary,
          metrics,
          insights,
          actions,
        };

        cardData["time-of-day"] = {
          value: favoriteName,
          change: {
            direction: "flat",
            value: favoriteStats.range,
            label: "window",
            tone: "neutral",
          },
        };
      } else {
        addNoData("time-of-day", "We need a few more runs to understand your time-of-day rhythm.");
      }
    }
  }
  const distanceDistribution = analytics.distance_distribution;
  if (distanceDistribution) {
    if (distanceDistribution.error) {
      const message = removeReplacementChars(distanceDistribution.error) || "Distance breakdown is not available yet.";
      addNoData("distance-distribution", message);
    } else {
      const distributionMap = distanceDistribution.distribution || {};
      const bucketOrder = ["0-1", "1-2", "2-4", "4-6", "6-10", "10+"];
      const bucketData = bucketOrder.map((bucket) => {
        const entry = distributionMap[bucket] || {};
        const countValue = Number.isFinite(entry.count) ? entry.count : 0;
        const percentageValue = Number.isFinite(entry.percentage) ? entry.percentage : 0;
        return { bucket, count: countValue, percentage: percentageValue };
      });
      const bucketMap = bucketData.reduce((acc, item) => {
        acc[item.bucket] = item;
        return acc;
      }, {});

      const stats = distanceDistribution.statistics || {};
      const comfortZoneBucket = bucketData
        .filter((item) => item.count > 0)
        .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))[0] || null;

      const histogramGroups = [
        { label: "0-2", ranges: ["0-1", "1-2"] },
        { label: "2-4", ranges: ["2-4"] },
        { label: "4-6", ranges: ["4-6"] },
        { label: "6-10", ranges: ["6-10"] },
        { label: "10+", ranges: ["10+"] },
      ];

      const histogramData = histogramGroups.map((group) => {
        const groupCount = group.ranges.reduce((sum, range) => {
          const entry = bucketMap[range];
          return sum + (entry ? entry.count : 0);
        }, 0);
        const groupPercentage = group.ranges.reduce((sum, range) => {
          const entry = bucketMap[range];
          return sum + (entry ? entry.percentage : 0);
        }, 0);
        return {
          label: group.label,
          count: groupCount,
          percentage: groupPercentage,
        };
      });

      const summaryParts = [];
      if (isFiniteNumber(stats.average)) {
        summaryParts.push("Your typical run averages " + formatDistance(stats.average) + ".");
      }
      if (comfortZoneBucket && isFiniteNumber(comfortZoneBucket.percentage)) {
        summaryParts.push(
          comfortZoneBucket.percentage.toFixed(1) +
            "% of runs land in the " +
            comfortZoneBucket.bucket +
            " " + getDistanceWord() + " comfort zone."
        );
      }
      if (isFiniteNumber(stats.max)) {
        summaryParts.push("Longest recent effort stretches to " + formatDistance(stats.max) + ".");
      }
      const summary = summaryParts.length ? summaryParts.join(" ") : "Keep logging runs so we can map your distance profile.";

      const metrics = [
        { label: "Average distance", value: formatDistance(stats.average) },
        { label: "Median distance", value: formatDistance(stats.median) },
        { label: "Shortest run", value: formatDistance(stats.min) },
        { label: "Longest run", value: formatDistance(stats.max) },
      ];
      if (comfortZoneBucket && isFiniteNumber(comfortZoneBucket.percentage)) {
        metrics.push({
          label: "Comfort zone",
          value: comfortZoneBucket.bucket + " " + getUnitLabel() + " | " + formatPercent(comfortZoneBucket.percentage, 1),
        });
      }

      const insights = cleanInsights(distanceDistribution.insights, unit);
      const actions = [];
      if (comfortZoneBucket) {
        actions.push("Keep a " + comfortZoneBucket.bucket + " " + getDistanceWord() + " fallback run ready for tight schedules.");
      } else {
        actions.push("Keep logging runs so we can identify your comfort zone.");
      }
      if (isFiniteNumber(stats.max) && isFiniteNumber(stats.average) && stats.max > stats.average) {
        actions.push("Plan dedicated long runs near " + formatDistance(stats.max) + " to keep extending capacity.");
      }

      detailData["distance-distribution"] = {
        title: "Distance mix",
        summary,
        metrics,
        insights,
        actions,
        histogramData,
      };

      let changeValue = "";
      if (isFiniteNumber(stats.max)) {
        changeValue = "Longest " + formatDistance(stats.max);
      }

      cardData["distance-distribution"] = {
        value: formatDistance(stats.average),
        change: {
          direction: "flat",
          value: changeValue,
          label: "range",
          tone: "neutral",
        },
        histogramData,
        summary,
      };
    }
  }
  const streakBehavior = analytics.streak_behavior;
  if (streakBehavior) {
    if (streakBehavior.error) {
      const message = removeReplacementChars(streakBehavior.error) || "We could not evaluate streak behavior yet.";
      addNoData("streak-behavior", message);
    } else {
      const totalRuns = streakBehavior.total_activities;
      const averageDistance = streakBehavior.average_distance;
      const stdDeviation = streakBehavior.std_deviation;
      const minimumEffortDays = streakBehavior.minimum_effort_days;
      const consistencyScore = streakBehavior.consistency_score;
      const consistencyPercent = isFiniteNumber(consistencyScore) ? consistencyScore * 100 : null;
      const minEffortPercent =
        Number.isFinite(totalRuns) && totalRuns > 0 && Number.isFinite(minimumEffortDays)
          ? (minimumEffortDays / totalRuns) * 100
          : null;

      const summaryParts = [];
      if (isFiniteNumber(consistencyPercent)) {
        summaryParts.push("Consistency score sits at " + formatPercent(consistencyPercent, 0) + ".");
      }
      if (isFiniteNumber(averageDistance)) {
        summaryParts.push("Average distance holds at " + formatDistance(averageDistance) + ".");
      }
      if (isFiniteNumber(minEffortPercent)) {
        summaryParts.push(minEffortPercent.toFixed(0) + "% of runs are short streak savers.");
      }
      const summary = summaryParts.length ? summaryParts.join(" ") : "We need more data to describe your streak behavior.";

      const metrics = [
        { label: "Total runs tracked", value: formatCount(totalRuns) },
        { label: "Consistency score", value: formatPercent(consistencyPercent, 0) },
        {
          label: "Minimum-effort runs",
          value: formatCount(minimumEffortDays) + " (" + formatPercent(minEffortPercent, 0) + ")",
        },
        {
          label: "Distance variation",
          value: isFiniteNumber(stdDeviation) ? "±" + convertFromMiles(stdDeviation).toFixed(1) + " " + getUnitLabel() : "—",
        },
      ];

      const insights = cleanInsights(streakBehavior.insights, unit);
      const actions = [
        "Keep a short fallback route ready for days when time is tight.",
      ];
      if (isFiniteNumber(stdDeviation)) {
        actions.push("Mix in sessions +/- " + stdDeviation.toFixed(1) + " mi from the average to maintain adaptability.");
      }

      detailData["streak-behavior"] = {
        title: "Streak durability",
        summary,
        metrics,
        insights,
        actions,
      };

      cardData["streak-behavior"] = {
        value: formatPercent(consistencyPercent, 0),
        change: {
          direction: "flat",
          value: formatCount(minimumEffortDays) + " fallback runs",
          label: "logged",
          tone: "neutral",
        },
      };
    }
  }
  const daylightPatterns = analytics.daylight_patterns;
  if (daylightPatterns) {
    if (daylightPatterns.error) {
      const message = removeReplacementChars(daylightPatterns.error) || "Daylight data is not available yet.";
      addNoData("daylight-patterns", message);
    } else {
      const breakdown = daylightPatterns.breakdown || {};
      const daylight = breakdown.daylight || {};
      const darkness = breakdown.darkness || {};

      const summaryParts = [];
      if (isFiniteNumber(daylight.percentage)) {
        summaryParts.push("Daylight runs cover " + formatPercent(daylight.percentage, 1) + " of your log.");
      }
      if (
        isFiniteNumber(darkness.percentage) &&
        isFiniteNumber(daylight.percentage) &&
        darkness.percentage > daylight.percentage
      ) {
        summaryParts.push("You lean towards running after dark.");
      }
      const summary = summaryParts.length ? summaryParts.join(" ") : "We are still collecting data on when you run.";

      const metrics = [
        { label: "Daylight share", value: formatPercent(daylight.percentage, 1) },
        { label: "Darkness share", value: formatPercent(darkness.percentage, 1) },
        { label: "Avg daylight distance", value: formatDistance(daylight.avg_distance) },
        { label: "Avg darkness distance", value: formatDistance(darkness.avg_distance) },
      ];

      const insights = cleanInsights(daylightPatterns.insights, unit);
      const actions = [];
      if (isFiniteNumber(daylight.percentage) && daylight.percentage >= 50) {
        actions.push("Protect daylight windows for key workouts and social long runs.");
      } else {
        actions.push("Keep reflective gear handy as a larger share of runs happen in low light.");
      }
      actions.push("Match recovery plans (sleep, fueling) with when you tend to log harder sessions.");

      detailData["daylight-patterns"] = {
        title: "Light conditions",
        summary,
        metrics,
        insights,
        actions,
      };

      const daylightValue = isFiniteNumber(daylight.percentage)
        ? daylight.percentage.toFixed(0) + "% daylight"
        : "Balanced";

      cardData["daylight-patterns"] = {
        value: daylightValue,
        change: {
          direction: "flat",
          value:
            formatCount(daylight.count || 0) +
            " daylight / " +
            formatCount(darkness.count || 0) +
            " dark",
          label: "sessions",
          tone: "neutral",
        },
      };
    }
  }
  const weatherPatterns = analytics.weather_patterns;
  if (weatherPatterns) {
    if (weatherPatterns.error) {
      const message = removeReplacementChars(weatherPatterns.error) || "Weather analytics require runs with weather data.";
      addNoData("weather-patterns", message);
    } else {
      const weatherBreakdown = weatherPatterns.weather_breakdown || {};
      const temperatureBreakdown = weatherPatterns.temperature_breakdown || {};
      const conditions = Object.entries(weatherBreakdown).filter(([, stats]) => {
        return Number.isFinite(stats && stats.count) && stats.count > 0;
      });
      const topCondition = conditions.length
        ? conditions.slice().sort((a, b) => (b[1].percentage || 0) - (a[1].percentage || 0))[0]
        : null;

      const rainShare = conditions
        .filter(([name]) => /rain|drizzle/i.test(name))
        .reduce((acc, [, stats]) => acc + (stats.percentage || 0), 0);
      const clearShare = conditions
        .filter(([name]) => /clear/i.test(name))
        .reduce((acc, [, stats]) => acc + (stats.percentage || 0), 0);

      const paceEntries = Object.entries(temperatureBreakdown).filter(([, stats]) => {
        return isFiniteNumber(stats && stats.avg_pace) && stats.avg_pace > 0;
      });
      const bestPaceEntry = paceEntries.length
        ? paceEntries.slice().sort((a, b) => (a[1].avg_pace || 0) - (b[1].avg_pace || 0))[0]
        : null;

      const summaryParts = [];
      if (topCondition) {
        summaryParts.push(
          topCondition[0] + " conditions cover " + formatPercent(topCondition[1].percentage, 1) + " of your runs."
        );
      }
      if (isFiniteNumber(rainShare) && rainShare > 0) {
        summaryParts.push("Rainy outings account for " + rainShare.toFixed(1) + "% of activity.");
      }
      if (bestPaceEntry) {
        const temperatureLabel = formatTemperatureBucket(Number(bestPaceEntry[0]));
        const paceLabel = formatPaceValue(bestPaceEntry[1].avg_pace);
        if (temperatureLabel && paceLabel) {
          summaryParts.push("Fastest pace shows up near " + temperatureLabel + " at " + paceLabel + ".");
        }
      }
      const summary = summaryParts.length ? summaryParts.join(" ") : "Log more runs with weather data to unlock insights.";

      const metrics = [];
      if (topCondition) {
        metrics.push({
          label: "Top condition",
          value: topCondition[0] + " • " + formatPercent(topCondition[1].percentage, 1),
        });
      }
      if (isFiniteNumber(clearShare)) {
        metrics.push({ label: "Clear sky share", value: clearShare.toFixed(1) + "%" });
      }
      if (isFiniteNumber(rainShare)) {
        metrics.push({ label: "Rain share", value: rainShare.toFixed(1) + "%" });
      }
      if (bestPaceEntry) {
        const temperatureLabel = formatTemperatureBucket(Number(bestPaceEntry[0]));
        const paceLabel = formatPaceValue(bestPaceEntry[1].avg_pace);
        if (temperatureLabel && paceLabel) {
          metrics.push({ label: "Best pace window", value: paceLabel + " at " + temperatureLabel });
        }
      }

      const insights = cleanInsights(weatherPatterns.insights, unit);
      const actions = [];
      if (topCondition) {
        actions.push("Schedule breakthrough sessions when forecasts call for " + topCondition[0].toLowerCase() + " conditions.");
      } else {
        actions.push("Keep logging weather data so we can highlight your optimal days.");
      }
      if (isFiniteNumber(rainShare) && rainShare > 0) {
        actions.push("Prep wet-weather gear; rainy runs make up a noticeable share of your log.");
      } else {
        actions.push("Maintain the habit of checking forecasts so unexpected swings do not derail plans.");
      }

      detailData["weather-patterns"] = {
        title: "Weather impact",
        summary,
        metrics,
        insights,
        actions,
      };

      cardData["weather-patterns"] = {
        value: topCondition ? topCondition[0] : "Balanced",
        change: topCondition
          ? {
              direction: "flat",
              value: formatPercent(topCondition[1].percentage, 1),
              label: "share",
              tone: "neutral",
            }
          : null,
      };
    }
  }
  if (!achievements) {
    addNoData("achievement-longest-run", "Connect your account to surface long run milestones.");
    addNoData("achievement-fastest-pace", "Sync more runs to lock in a fastest " + getDistanceWord() + " pace record.");
    addNoData("achievement-perfect-weeks", "Keep logging runs to uncover perfect-week streaks.");
  } else {
    const longestRun = achievements.longest_run;
    const longestRunDistance = longestRun ? parseNumber(longestRun.distance_miles) : null;
    if (longestRun && isFiniteNumber(longestRunDistance) && longestRunDistance > 0) {
      const formattedDistance = formatDistance(longestRunDistance);
      const runDateLabel = formatDateLong(longestRun.date);
      const summaryParts = [];
      summaryParts.push(
        "Your longest streak run stretches " + formattedDistance + (runDateLabel ? " (" + runDateLabel + ")" : "") + "."
      );
      if (longestRun.name) {
        summaryParts.push("Route highlight: " + longestRun.name + ".");
      }
      const summary = summaryParts.join(" ");
      const metrics = [
        { label: "Distance", value: formattedDistance },
      ];
      if (runDateLabel) {
        metrics.push({ label: "Logged on", value: runDateLabel });
      }
      if (longestRun.name) {
        metrics.push({ label: "Route", value: longestRun.name });
      }
      const actions = ["Plan a dedicated long run block to build on this benchmark effort."];
      detailData["achievement-longest-run"] = {
        title: "Longest recorded run",
        summary,
        metrics,
        insights: [],
        actions,
      };
      cardData["achievement-longest-run"] = {
        value: formattedDistance,
        change: runDateLabel
          ? {
              direction: "flat",
              value: runDateLabel,
              label: "logged",
              tone: "neutral",
            }
          : null,
      };
    } else {
      addNoData(
        "achievement-longest-run",
        "We need at least one completed run with distance to highlight your longest effort."
      );
    }

    const fastestPace = achievements.fastest_mile_pace;
    const fastestDistance = fastestPace ? parseNumber(fastestPace.distance_miles) : null;
    const fastestPaceValue = fastestPace && fastestPace.pace_min_per_mile ? fastestPace.pace_min_per_mile : null;
    if (fastestPaceValue) {
      const paceLabel = formatPaceValue(fastestPaceValue, 2);
      if (paceLabel) {
        const distanceLabel = isFiniteNumber(fastestDistance) ? formatDistance(fastestDistance) : null;
        const paceDate = formatDateLong(fastestPace.date);
        const summaryParts = ["Fastest " + getDistanceWord() + " pace locks in at " + paceLabel + "."];
        if (distanceLabel) {
          summaryParts.push("Effort distance: " + distanceLabel + ".");
        }
        if (paceDate) {
          summaryParts.push("Logged on " + paceDate + ".");
        }
        const summary = summaryParts.join(" ");
        const metrics = [{ label: "Pace", value: paceLabel }];
        if (distanceLabel) {
          metrics.push({ label: "Distance", value: distanceLabel });
        }
        if (paceDate) {
          metrics.push({ label: "Logged on", value: paceDate });
        }
        const actions = ["Use interval or tempo sessions to continue nudging this pace faster."];
        detailData["achievement-fastest-pace"] = {
          title: "Fastest recorded pace",
          summary,
          metrics,
          insights: [],
          actions,
        };
        cardData["achievement-fastest-pace"] = {
          value: paceLabel,
          change: distanceLabel
            ? {
                direction: "flat",
                value: distanceLabel,
                label: "distance",
                tone: "neutral",
              }
            : null,
        };
      } else {
        addNoData(
          "achievement-fastest-pace",
          "We could not format your pace. Try again after syncing new activities."
        );
      }
    } else {
      addNoData(
        "achievement-fastest-pace",
        "We have not seen a fastest " + getDistanceWord() + " pace yet—log more runs with pace data to unlock this metric."
      );
    }

    const perfectWeeks = achievements.perfect_weeks;
    const perfectWeekCount = perfectWeeks ? parseNumber(perfectWeeks.count) : null;
    if (Number.isFinite(perfectWeekCount) && perfectWeekCount > 0) {
      const weeksLabel = formatCountLabel(perfectWeekCount, "week", "weeks");
      const summary = "You have completed " + weeksLabel + " with seven runs in seven consecutive days.";
      const metrics = [{ label: "Perfect weeks", value: weeksLabel }];
      const multipleRunDays = achievements.multiple_run_days;
      if (multipleRunDays && Number.isFinite(multipleRunDays.count)) {
        metrics.push({
          label: "Multiple-run days",
          value: formatCountLabel(multipleRunDays.count, "day", "days"),
        });
      }
      const doubles = achievements.doubles;
      if (doubles && Number.isFinite(doubles.count)) {
        metrics.push({
          label: "Midnight doubles",
          value: formatCountLabel(doubles.count, "time", "times"),
        });
      }
      const actions = [
        "Keep recovery runs planned so you can sustain weekly streak momentum.",
        "Block calendar reminders near the tail-end of each week to protect your final runs.",
      ];
      detailData["achievement-perfect-weeks"] = {
        title: "Perfect week streaks",
        summary,
        metrics,
        insights: [],
        actions,
      };
      cardData["achievement-perfect-weeks"] = {
        value: weeksLabel,
        change: null,
      };
    } else {
      addNoData(
        "achievement-perfect-weeks",
        "Complete seven days in a row to unlock perfect week tracking."
      );
    }
  }
  return { cardData, detailData };
};

const Analytics = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter] = useState("All Categories");
  const [timeFilter, setTimeFilter] = useState("This Month");
  const [timeFilterOpen, setTimeFilterOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(DEFAULT_CARD_ID);
  const [activeSection, setActiveSection] = useState(ANALYTICS_SECTIONS[0]?.id || "patterns");
  const [profileData, setProfileData] = useState(null);
  const userEmail = user && user.email ? user.email : null;
  const { unit, getUnitLabel } = useDistanceUnit();

  // Helper to convert miles to user's preferred unit
  const convertFromMiles = useCallback((miles) => {
    if (!miles || !Number.isFinite(miles)) return miles;
    if (unit === 'km') {
      return miles * 1.60934; // Convert miles to km
    }
    return miles; // Already in miles
  }, [unit]);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get('/profile');
      setProfileData(response.data.profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchAnalytics = async () => {
      if (!userEmail) {
        if (isMounted) {
          setAnalytics(null);
          setAchievements(null);
          setError("Sign in to view analytics.");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const [analyticsResult, achievementsResult] = await Promise.allSettled([
          apiClient.get('/analytics/patterns', {
            params: { unit },
          }),
          apiClient.get('/analytics/achievements', {
            params: { unit },
          }),
        ]);

        if (!isMounted) {
          return;
        }

        if (analyticsResult.status === "fulfilled") {
          setAnalytics(analyticsResult.value.data);
        } else {
          const message = getErrorMessage(
            analyticsResult.reason,
            "Unable to fetch analytics right now."
          );
          setAnalytics(null);
          setError(removeReplacementChars(message));
        }

        if (achievementsResult.status === "fulfilled") {
          setAchievements(achievementsResult.value.data);
        } else {
          console.error("Error fetching achievements:", achievementsResult.reason);
          setAchievements(null);
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error("Unexpected error fetching analytics:", err);
        const message = getErrorMessage(err, "Unable to fetch analytics right now.");
        setError(removeReplacementChars(message));
        setAnalytics(null);
        setAchievements(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAnalytics();

    return () => {
      isMounted = false;
    };
  }, [userEmail, unit]);
  const derivedContent = useMemo(() => buildAnalyticsContent(analytics, achievements, convertFromMiles, getUnitLabel, unit), [analytics, achievements, convertFromMiles, getUnitLabel, unit]);
  const cardOverrides = derivedContent.cardData;
  const detailOverrides = derivedContent.detailData;

  const selectedCard = useMemo(() => {
    if (!selectedCardId) {
      return null;
    }
    const baseCard = CARD_LOOKUP[selectedCardId];
    if (!baseCard) {
      return null;
    }
    const override = cardOverrides[selectedCardId] || {};
    return {
      ...baseCard,
      value: override.value !== undefined ? override.value : baseCard.value,
      badge: override.badge !== undefined ? override.badge : baseCard.badge,
      change: Object.prototype.hasOwnProperty.call(override, "change") ? override.change : baseCard.change,
    };
  }, [selectedCardId, cardOverrides]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    // Reset selected card to first card of new section
    const section = ANALYTICS_SECTIONS.find(s => s.id === sectionId);
    if (section && section.cards.length > 0) {
      setSelectedCardId(section.cards[0].id);
    }
  };

  const activeSectionData = ANALYTICS_SECTIONS.find(s => s.id === activeSection);

  const getCardTitle = (card) => {
    if (card.id === 'achievement-fastest-pace') {
      return 'Fastest ' + (unit === 'km' ? 'Kilometer' : 'Mile') + ' Pace';
    }
    return card.title;
  };

  const renderChange = (change) => {
    if (!change || (!change.value && change.value !== 0)) {
      return null;
    }
    const direction = change.direction;
    const value = change.value;
    const label = change.label;
    const tone = change.tone || "positive";
    const arrowColor = tone === "negative" ? "#d32f2f" : tone === "neutral" ? "#4a5565" : "#00a63e";
    const valueClass =
      tone === "negative" ? "analytics-card-change-value negative" : tone === "neutral" ? "analytics-card-change-value neutral" : "analytics-card-change-value";

    return (
      <div className="analytics-card-change">
        {direction && direction !== "flat" && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            {direction === "down" ? (
              <path
                d="M6 3V9M6 9L3 6M6 9L9 6"
                stroke={arrowColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="M6 9V3M6 3L3 6M6 3L9 6"
                stroke={arrowColor}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        )}
        <span className={valueClass}>{value}</span>
        {label && <span className="analytics-card-change-period">{label}</span>}
      </div>
    );
  };
  const renderDetail = () => {
    if (!selectedCard) {
      return (
        <div className="analytics-detail-card">
          <div className="analytics-detail-header">
            <h2 className="analytics-detail-title">Select an insight</h2>
            <p className="analytics-detail-summary">
              Tap any card to unlock supporting metrics, highlights, and suggested next steps.
            </p>
          </div>
        </div>
      );
    }

    const detail = detailOverrides[selectedCardId];
    const detailTitle = detail && detail.title ? detail.title : selectedCard.title;
    const summary = detail && detail.summary ? detail.summary : "Analytics will appear here once we have enough data to analyze.";
    const histogramData =
      detail && Array.isArray(detail.histogramData) ? detail.histogramData.filter((bucket) => bucket) : null;

    return (
      <div className="analytics-detail-card">
        <div className="analytics-detail-header">
          <span className="analytics-detail-chip">
            {selectedCard.badge && (
              <span className={"analytics-badge analytics-badge--" + selectedCard.badge}>{selectedCard.badge}</span>
            )}
            {selectedCard.sectionTitle}
          </span>
          <h2 className="analytics-detail-title">{detailTitle}</h2>
          <p className="analytics-detail-summary">{summary}</p>
        </div>

        {histogramData && histogramData.length > 0 && (
          <div
            className="analytics-detail-histogram"
            aria-label="Distance distribution histogram by run length buckets"
          >
            <ul className="analytics-histogram-bars">
              {histogramData.map((bucket) => {
                const rawPercentage = Number.isFinite(bucket.percentage) ? bucket.percentage : 0;
                const hasRuns = Number.isFinite(bucket.count) && bucket.count > 0;
                const adjustedWidth = Math.min(100, Math.max(hasRuns ? 6 : 0, rawPercentage));
                return (
                  <li key={bucket.label} className="analytics-histogram-row">
                    <span className="analytics-histogram-label">{bucket.label} {getUnitLabel()}</span>
                    <div className="analytics-histogram-bar-track" aria-hidden="true">
                      <div
                        className="analytics-histogram-bar-fill"
                        style={{ width: adjustedWidth + "%" }}
                      />
                    </div>
                    <span className="analytics-histogram-value">
                      {formatCount(bucket.count)} runs
                      <span className="analytics-histogram-percentage">
                        {formatPercent(rawPercentage, 1)}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {detail && detail.metrics && detail.metrics.length > 0 && (
          <div className="analytics-detail-metrics">
            {detail.metrics.map((metric) => (
              <div key={metric.label} className="analytics-detail-metric">
                <span className="analytics-detail-metric-label">{metric.label}</span>
                <span className="analytics-detail-metric-value">{metric.value}</span>
              </div>
            ))}
          </div>
        )}

        {detail && detail.insights && detail.insights.length > 0 && (
          <div className="analytics-detail-list">
            <h3>Key insights</h3>
            <ul>
              {detail.insights.map((insight, index) => (
                <li key={index}>{insight}</li>
              ))}
            </ul>
          </div>
        )}

        {detail && detail.actions && detail.actions.length > 0 && (
          <div className="analytics-detail-list">
            <h3>Suggested next steps</h3>
            <ul>
              {detail.actions.map((action, index) => (
                <li key={index}>{action}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  if (loading) {
    return (
      <div className="analytics-page">
        <Sidebar isOpen={sidebarOpen} />
        <div className="analytics-loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-page">
        <Sidebar isOpen={sidebarOpen} />
        <div className="analytics-error">
          <h2>Unable to Load Analytics</h2>
          <p>{error}</p>
          <p>Please try refreshing the page or contact support if the problem persists.</p>
        </div>
      </div>
    );
  }

  const containerClass = sidebarOpen
    ? "analytics-main-container"
    : "analytics-main-container sidebar-closed";
  return (
    <div className="analytics-page">
      <Sidebar isOpen={sidebarOpen} />

      <div className={containerClass}>
        <header className="dashboard-header">
          <div className="header-left">
            <button className="icon-button" onClick={toggleSidebar}>
              <img src={iconMenu} alt="Menu" />
            </button>
            <h1 className="header-title">Runstreak Dashboard</h1>
          </div>
          <div className="header-right">
            <div className="header-search-container">
              <img src={iconSearch} alt="Search" className="search-icon-header" />
              <input type="text" placeholder="Search activities..." className="header-search-input" />
            </div>
            <NotificationBell />
            <button className="logout-button" onClick={onLogout}>
              Logout
            </button>
            <ProfilePicture
              profilePictureUrl={profileData?.profile_picture_url}
              firstName={profileData?.first_name}
              lastName={profileData?.last_name}
              email={user?.email}
              size="small"
              showInitials={true}
            />
          </div>
        </header>

        <div className="analytics-content">
          <div className="analytics-header-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h1 className="analytics-title">Analytics</h1>
                <p className="analytics-subtitle">Deep insights into your running performance</p>
              </div>
              <div className="analytics-filters">
                <button className="analytics-filter-button">
                  <span>{categoryFilter}</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div style={{ position: "relative" }}>
                  <button
                    className="analytics-filter-button"
                    onClick={() => setTimeFilterOpen(!timeFilterOpen)}
                  >
                    <span>{timeFilter}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {timeFilterOpen && (
                    <div className="analytics-filter-dropdown">
                      {["This Month", "Last Month", "All Activities"].map((option) => (
                        <button
                          key={option}
                          className={`analytics-filter-option ${timeFilter === option ? "active" : ""}`}
                          onClick={() => {
                            setTimeFilter(option);
                            setTimeFilterOpen(false);
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && <div className="analytics-error-banner">{error}</div>}

          {/* Section Tabs */}
          <div className="analytics-section-tabs">
            {ANALYTICS_SECTIONS.map((section) => (
              <button
                key={section.id}
                className={`analytics-section-tab ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => handleSectionChange(section.id)}
              >
                {section.title}
              </button>
            ))}
          </div>

          <div className="analytics-layout">
            {activeSectionData && (
              <section key={activeSectionData.id} className="analytics-section">
                <div className="analytics-section-header">
                  <h2 className="analytics-section-title">{activeSectionData.title}</h2>
                  <p className="analytics-section-subtitle">{activeSectionData.description}</p>
                </div>
                <div className="analytics-card-grid">
                    {activeSectionData.cards.map((card) => {
                      const override = cardOverrides[card.id] || {};
                      const cardValue = override.value !== undefined ? override.value : card.value;
                      const cardBadge = override.badge !== undefined ? override.badge : card.badge;
                      const change = Object.prototype.hasOwnProperty.call(override, "change") ? override.change : card.change;
                      const isActive = selectedCardId === card.id;
                      const isDistanceCard = card.id === "distance-distribution";
                      const baseClass = isActive ? "analytics-card analytics-card--active" : "analytics-card";
                      const cardClass = isDistanceCard ? baseClass + " analytics-card--distance" : baseClass;
                      const variant = card.variant ? card.variant : "standard";
                      const summaryText = override.summary || card.summary || card.description;

                      if (isDistanceCard) {

                        return (
                          <button
                            key={card.id}
                            type="button"
                            data-variant={variant}
                            className={cardClass}
                            onClick={() => setSelectedCardId(card.id)}
                            aria-pressed={isActive}
                          >
                            <div className="analytics-card-header">
                              <div className="analytics-card-icon-badge">
                                {CARD_ICONS[card.icon]}
                                {cardBadge && (
                                  <span className={"analytics-badge analytics-badge--" + cardBadge}>{cardBadge}</span>
                                )}
                              </div>
                            </div>
                            <div className="analytics-card-distance-body">
                              <div className="analytics-card-distance-info">
                                <h3 className="analytics-card-title">{getCardTitle(card)}</h3>
                                <div className="analytics-card-value-row">
                                  <div className="analytics-card-value">{cardValue}</div>
                                  {renderChange(change)}
                                </div>
                                <p className="analytics-card-summary">{summaryText}</p>
                              </div>
                            </div>
                          </button>
                        );
                      }

                      return (
                        <button
                          key={card.id}
                          type="button"
                          data-variant={variant}
                          className={cardClass}
                          onClick={() => setSelectedCardId(card.id)}
                          aria-pressed={isActive}
                        >
                          <div className="analytics-card-header">
                            <div className="analytics-card-icon-badge">
                              {CARD_ICONS[card.icon]}
                              {cardBadge && (
                                <span className={"analytics-badge analytics-badge--" + cardBadge}>{cardBadge}</span>
                              )}
                            </div>
                          </div>
                          <h3 className="analytics-card-title">{getCardTitle(card)}</h3>
                          <div className="analytics-card-value">{cardValue}</div>
                          {renderChange(change)}
                          <p className="analytics-card-description">{card.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Detail Panel Below Cards */}
              <div className="analytics-detail-panel">{renderDetail()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
